import MembershipApi from '_services/Api/Membership';
import { UserShape } from '_types/User';
import { AxiosResponse } from 'axios';
import useNavigate from 'Hooks/useNavigate';
import useQuery from './useQuery';

import { baseUrl } from '_config';
import { isUrlProtected } from '_router/Router';
import OpenCheckoutPlan from '_services/LocalForage/OpenCheckoutPlan';
import Subscription from '_services/LocalStorage/Subscription';
import { persistCheckoutMetaEventId } from './useStripeCheckout';
import { generateEventId, getMetaPixelIds } from './useStripeCheckout.helpers';

type handleLoggedInShape = {
    user: UserShape;
    setIsLoading?: (isLoading: boolean) => void;
};

const useCheckoutAfterLogin = (): (({
    user,
    setIsLoading,
}: handleLoggedInShape) => void)[] => {
    const navigate = useNavigate();
    const lastLocation = useQuery().get('lastLocation');

    /**
     * Handling login with checkout
     */
    const handleLoggedIn = async ({
        user,
        setIsLoading,
    }: handleLoggedInShape) => {
        const cancelLoading = () => {
            if (setIsLoading) {
                setIsLoading(false);
            }
        };

        const handleApiResponse = (response: AxiosResponse) => {
            const { url, meta_event_id } = response.data;
            if (url) {
                persistCheckoutMetaEventId(meta_event_id);
                cancelLoading();
                window.location.href = url;
            }
        };

        const storedMembership = await OpenCheckoutPlan.get();
        const params = JSON.parse(storedMembership ?? `{}`);

        // When user selected any subscription
        if (params?.product_id && params?.interval) {
            params.return_url = baseUrl;

            // when user already have the same subscription
            if (
                (`membership_${user?.membership_summary?.type}` ===
                    params?.product_id ||
                    user?.membership_summary?.type === params?.product_id) &&
                params?.interval === user?.membership_summary?.interval
            ) {
                OpenCheckoutPlan.remove();
                navigate('/', {
                    state: {
                        showCheckoutPopup: true,
                    },
                });
                return;
            }

            const { fbp, fbc } = getMetaPixelIds();
            const event_id = generateEventId();

            MembershipApi.getCheckoutUrl({
                ...params,
                ...(fbp ? { fbp } : {}),
                ...(fbc ? { fbc } : {}),
                ...(event_id ? { event_id } : {}),
            })
                .then((response) => {
                    Subscription.set(
                        JSON.stringify({
                            type: params.product_id,
                            interval: params.interval,
                        }),
                    );
                    handleApiResponse(response);
                })
                .catch((error) => {
                    console.error('Error in checkout api', error);
                    cancelLoading();
                    navigate('/');
                });
            return;
        }
        cancelLoading();

        const getRedirectPath = (): string => {
            if (lastLocation && isUrlProtected(lastLocation)) {
                return lastLocation;
            }
            if (user?.should_see_welcome) {
                return '/welcome-new';
            }
            return '/';
        };
        navigate(getRedirectPath());
    };

    return [handleLoggedIn];
};

export default useCheckoutAfterLogin;
